import { useMemo } from "react";
import { budgetNonEditableReasons, ENVS, tooltips } from "config";
import { useSessionUser } from "hooks/useSession";
import { useProjectsApi } from "hooks/useProjects";
import { useProjectAuth } from "hooks/project/useProjectAuth";
import { useProjectIdentifiers } from "store/hooks/useProjectIdentifiers";
import { isAiFeatureStatusRunning } from "@/components/common/AiPipelineRunning";
import { getProjectFeatureStatus, mapProjectFeatureStatusesToFeatureStatus } from "utils";

export type RebudgetBlockKind = "locked" | "viewer" | "trial" | "freePlan" | "aiRunning";

export interface RebudgetBlock {
  kind: RebudgetBlockKind;
  /** The header's wording for why Rebudget is unavailable. */
  message: string;
}

export interface RebudgetGate {
  /** Why this person cannot start an AI rebudget right now, or null when they can. */
  block: RebudgetBlock | null;
  isViewer: boolean;
  isTrialProject: boolean;
  /** A free subscription plan without a one-time license. */
  isFreePlanBlocked: boolean;
  isBudgetAiRunning: boolean;
}

/**
 * The one rule for who may start an AI rebudget: the header's Rebudget button and the
 * stale-budget notice's actions both read it, so they can never disagree.
 */
export function useRebudgetGate(budgetLocked: boolean): RebudgetGate {
  const { projectId, project } = useProjectIdentifiers();
  const { isViewer } = useProjectAuth(projectId);
  const { useProject } = useProjectsApi({});
  const { data: projectData } = useProject(projectId);
  const { session: profile, isOneTimePurchase } = useSessionUser();

  const budgetFeature = useMemo(
    () =>
      getProjectFeatureStatus(
        mapProjectFeatureStatusesToFeatureStatus(
          project?.featureStatuses,
          project?.projectId ?? projectId ?? 0
        ),
        "budget"
      ),
    [project?.featureStatuses, project?.projectId, projectId]
  );
  const isBudgetAiRunning = isAiFeatureStatusRunning(budgetFeature?.status);
  const isTrialProject = Boolean(projectData?.isTrial);
  const isFreePlanBlocked =
    !isOneTimePurchase && profile?.pricing_plan_id === Number(ENVS.freePlanId);

  const block: RebudgetBlock | null = budgetLocked
    ? { kind: "locked", message: budgetNonEditableReasons.budgetLocked }
    : isViewer
      ? { kind: "viewer", message: "You are a viewer. Ask the owner to upgrade your role." }
      : isTrialProject
        ? {
            kind: "trial",
            message:
              "Rebudgeting is not available for trial projects. Purchase a license to unlock this feature.",
          }
        : isFreePlanBlocked
          ? { kind: "freePlan", message: tooltips.freePlan.upgradeSubscription }
          : isBudgetAiRunning
            ? { kind: "aiRunning", message: "Unavailable while AI budget is running." }
            : null;

  return { block, isViewer, isTrialProject, isFreePlanBlocked, isBudgetAiRunning };
}
